import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import {
  CatalogService,
  ComposeStorefrontGrowthPromptDiagnostic,
  StorefrontContentProfile,
  StorefrontProfileContactMethod,
  StorefrontProfileFaqItem,
  StorefrontProfileHighlight,
  StorefrontProfileLink,
  StorefrontProfileMetadata,
  StorefrontProfileVersionSummary
} from './catalog.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { StructuredJsonFieldDef } from '../../shared/design-system/structured-json-form.model';
import { StructuredJsonFormComponent } from '../../shared/design-system/structured-json-form.component';

const EMPTY_PROFILE = (): StorefrontContentProfile => ({
  brandName: '',
  tagline: '',
  shortDescription: '',
  phones: [],
  emails: [],
  address: '',
  hours: '',
  socialLinks: {},
  aboutHeadline: '',
  aboutBody: '',
  aboutHighlights: [],
  faqItems: [],
  contactSupportText: '',
  contactMethods: [],
  headerTopBarMessage: '',
  headerQuickLinks: [],
  footerBrandText: '',
  footerQuickLinks: [],
  footerCopyright: ''
});

const EMPTY_METADATA = (): StorefrontProfileMetadata => ({
  seo: { includeOrganization: true, includeFaqPage: true, includeContactPage: true },
  compliance: { region: '', privacyNotice: '', legalDisclaimer: '', regionalDisclaimer: '', legalEntityId: '', contactComplianceText: '' },
  abExperiment: { status: 'Draft', trafficSplitPercent: 50, heroOrderVariantB: '', ctaVariantB: '', faqOrderVariantB: '' },
  accessibility: { passesWcagAa: true, issues: [] }
});

const normalizeStorefrontProfileMetadata = (
  metadata?: StorefrontProfileMetadata | null
): StorefrontProfileMetadata => {
  const defaults = EMPTY_METADATA();
  if (!metadata) {
    return defaults;
  }

  const accessibilitySource = metadata.accessibility ?? defaults.accessibility!;
  return {
    seo: { ...defaults.seo, ...(metadata.seo ?? defaults.seo) },
    compliance: { ...defaults.compliance!, ...(metadata.compliance ?? {}) },
    abExperiment: { ...defaults.abExperiment!, ...(metadata.abExperiment ?? {}) },
    accessibility: {
      ...defaults.accessibility!,
      ...accessibilitySource,
      issues: accessibilitySource.issues ?? []
    }
  };
};

const LOCALE_OPTIONS = [
  { label: 'fa-IR', value: 'fa-IR' },
  { label: 'en-US', value: 'en-US' },
  { label: 'ar-SA', value: 'ar-SA' }
];

interface StorefrontAiPreviewState {
  profile: StorefrontContentProfile;
  metadata: StorefrontProfileMetadata;
  guardrailViolations: { code: string; message: string }[];
  provider: string;
  usedFallback: boolean;
  requiresApproval: boolean;
  phonesText: string;
  emailsText: string;
  socialLinksJson: string;
  highlightPayloads: string[];
  faqPayloads: string[];
  contactPayloads: string[];
  headerLinkPayloads: string[];
  footerLinkPayloads: string[];
}

@Component({
  selector: 'ain-storefront-profile-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, ConfirmDialogModule, InputTextModule, TextareaModule, SelectModule, InfoHintComponent, StructuredJsonFormComponent],
  providers: [ConfirmationService],
  template: `
    <ain-info-hint
      [titleKey]="helpTitleKey()"
      [descriptionKey]="helpDescriptionKey()"
      [bulletKeys]="helpBulletKeys()" />

    <section class="lifecycle-toolbar">
      <label>{{ t('catalog.storefront.profile.locale.label') }}
        <p-select [options]="localeOptions" [(ngModel)]="selectedLocale" optionLabel="label" optionValue="value" appendTo="body" (ngModelChange)="loadLifecycleState()" />
      </label>
      <div class="lifecycle-actions">
        <button pButton type="button" size="small" [severity]="viewMode === 'draft' ? 'primary' : 'secondary'" [label]="t('catalog.storefront.profile.view.draft')" (click)="setViewMode('draft')"></button>
        <button pButton type="button" size="small" [severity]="viewMode === 'published' ? 'primary' : 'secondary'" [label]="t('catalog.storefront.profile.view.published')" (click)="setViewMode('published')"></button>
        <button pButton type="button" size="small" severity="secondary" [label]="t('catalog.storefront.profile.action.saveDraft')" [disabled]="acting() || viewMode !== 'draft'" (click)="saveDraft()"></button>
        <button pButton type="button" size="small" severity="success" [label]="t('catalog.storefront.profile.action.publish')" [disabled]="acting() || !canPublish()" (click)="publishDraft()"></button>
        @if (draftState()?.isAiGenerated) {
          <button pButton type="button" size="small" severity="secondary" [label]="t('catalog.storefront.profile.action.submitApproval')" [disabled]="acting()" (click)="submitApproval()"></button>
          <button pButton type="button" size="small" severity="success" [label]="t('catalog.storefront.profile.action.approve')" [disabled]="acting()" (click)="approveDraft()"></button>
          <button pButton type="button" size="small" severity="danger" [label]="t('catalog.storefront.profile.action.reject')" [disabled]="acting()" (click)="rejectDraft()"></button>
        }
      </div>
      @if (statusNotice()) { <p class="notice">{{ statusNotice() }}</p> }
      @if (draftState()?.approvalNotes) { <p class="notice">{{ draftState()?.approvalNotes }}</p> }
    </section>

    @if (guardrailViolations().length > 0) {
      <section class="guardrail-panel">
        <h4>{{ t('catalog.storefront.profile.guardrails.title') }}</h4>
        <ul>
          @for (item of guardrailViolations(); track item.code + item.message) {
            <li>{{ item.message }}</li>
          }
        </ul>
      </section>
    }

    @if (metadata.accessibility && !metadata.accessibility.passesWcagAa) {
      <section class="guardrail-panel guardrail-panel--warn">
        <h4>{{ t('catalog.storefront.profile.accessibility.title') }}</h4>
        <ul>
          @for (issue of metadata.accessibility.issues; track issue.pair) {
            <li>{{ issue.pair }}: {{ issue.ratio | number:'1.2-2' }} / {{ issue.requiredRatio | number:'1.2-2' }}</li>
          }
        </ul>
      </section>
    }

    <section class="meta-section">
      <h4>{{ t('catalog.storefront.profile.section.compliance') }}</h4>
      <label>{{ t('catalog.storefront.profile.compliance.region') }}<input pInputText [(ngModel)]="metadata.compliance!.region" class="full" /></label>
      <label>{{ t('catalog.storefront.profile.compliance.privacyNotice') }}<textarea pTextarea rows="2" [(ngModel)]="metadata.compliance!.privacyNotice" class="full"></textarea></label>
      <label>{{ t('catalog.storefront.profile.compliance.legalDisclaimer') }}<textarea pTextarea rows="2" [(ngModel)]="metadata.compliance!.legalDisclaimer" class="full"></textarea></label>
      <label>{{ t('catalog.storefront.profile.compliance.regionalDisclaimer') }}<textarea pTextarea rows="2" [(ngModel)]="metadata.compliance!.regionalDisclaimer" class="full"></textarea></label>
      <label>{{ t('catalog.storefront.profile.compliance.legalEntityId') }}<input pInputText [(ngModel)]="metadata.compliance!.legalEntityId" class="full" /></label>
    </section>

    <section class="meta-section">
      <h4>{{ t('catalog.storefront.profile.section.experiment') }}</h4>
      <label>{{ t('catalog.storefront.profile.experiment.status') }}<input pInputText [(ngModel)]="metadata.abExperiment!.status" class="full" /></label>
      <label>{{ t('catalog.storefront.profile.experiment.trafficSplit') }}<input pInputText type="number" [(ngModel)]="metadata.abExperiment!.trafficSplitPercent" class="full" /></label>
      <label>{{ t('catalog.storefront.profile.experiment.heroVariantB') }}<input pInputText [(ngModel)]="metadata.abExperiment!.heroOrderVariantB" class="full" /></label>
      <label>{{ t('catalog.storefront.profile.experiment.ctaVariantB') }}<input pInputText [(ngModel)]="metadata.abExperiment!.ctaVariantB" class="full" /></label>
      <label>{{ t('catalog.storefront.profile.experiment.faqOrderVariantB') }}<input pInputText [(ngModel)]="metadata.abExperiment!.faqOrderVariantB" class="full" /></label>
    </section>

    <section class="meta-section">
      <h4>{{ t('catalog.storefront.profile.section.seo') }}</h4>
      <label><input type="checkbox" [(ngModel)]="metadata.seo.includeOrganization" /> {{ t('catalog.storefront.profile.seo.organization') }}</label>
      <label><input type="checkbox" [(ngModel)]="metadata.seo.includeFaqPage" /> {{ t('catalog.storefront.profile.seo.faqPage') }}</label>
      <label><input type="checkbox" [(ngModel)]="metadata.seo.includeContactPage" /> {{ t('catalog.storefront.profile.seo.contactPage') }}</label>
    </section>

    @if (versions().length > 0) {
      <section class="version-history">
        <h4>{{ t('catalog.storefront.profile.versions.title') }}</h4>
        <ul>
          @for (version of versions(); track version.id) {
            <li>
              v{{ version.versionNumber }} · {{ version.locale }} · {{ version.status }} · {{ version.approvalStatus }}
              <button pButton type="button" size="small" [text]="true" [label]="t('catalog.storefront.profile.action.rollback')" (click)="rollback(version)"></button>
            </li>
          }
        </ul>
      </section>
    }

    @if (editorMode === 'store' && storeId) {
    <section class="ai-section">
      <h4>{{ t('catalog.storefront.profile.ai.title') }}</h4>
      <ain-info-hint
        [titleKey]="'catalog.storefront.profile.ai.growthHint.title'"
        [descriptionKey]="'catalog.storefront.profile.ai.growthHint.description'"
        [bulletKeys]="[
          'catalog.storefront.profile.ai.growthHint.bulletManual',
          'catalog.storefront.profile.ai.growthHint.bulletGrowth',
          'catalog.storefront.profile.ai.growthHint.bulletReview'
        ]" />
      <div class="prompt-head">
        <label>{{ t('catalog.storefront.profile.ai.prompt') }}</label>
        <div class="prompt-actions">
          <button pButton type="button" size="small" severity="secondary" [text]="true" [label]="t('catalog.storefront.profile.ai.useTemplate')" (click)="applyPromptTemplate()"></button>
          <button pButton type="button" size="small" severity="secondary" [text]="true" [label]="t('catalog.storefront.profile.ai.buildFromGrowth')" [disabled]="composingGrowthPrompt() || generating() || applyingPreview()" (click)="buildFromGrowthData()"></button>
        </div>
      </div>
      <p class="prompt-help">{{ t('catalog.storefront.profile.ai.promptHelp') }}</p>
      <textarea pTextarea rows="8" [(ngModel)]="aiPrompt" class="full"></textarea>
      @if (growthDiagnostics().length > 0) {
        <div class="growth-diagnostics">
          <div class="growth-diagnostics-head">
            <strong>{{ t('catalog.storefront.profile.ai.growthDiagnostics.title') }}</strong>
            <span>{{ growthCompleteness() }}% · {{ t('catalog.storefront.profile.ai.growthDiagnostics.completenessSuffix') }}</span>
          </div>
          <ul>
            @for (item of growthDiagnostics(); track item.sectionKey) {
              <li [class]="growthDiagnosticClass(item)">
                <span>{{ growthDiagnosticLabel(item) }}</span>
                <span class="growth-diagnostic-status">{{ growthDiagnosticStatus(item) }}</span>
              </li>
            }
          </ul>
        </div>
      }
      <div class="ai-grid">
        <label>{{ t('catalog.storefront.profile.ai.industry') }}<input pInputText [(ngModel)]="aiIndustry" class="full" /></label>
        <label>{{ t('catalog.storefront.profile.ai.audience') }}<input pInputText [(ngModel)]="aiAudience" class="full" /></label>
        <label>{{ t('catalog.storefront.profile.ai.tone') }}<input pInputText [(ngModel)]="aiTone" class="full" /></label>
        <label>{{ t('catalog.storefront.profile.ai.language') }}<input pInputText [(ngModel)]="aiLanguage" class="full" /></label>
      </div>
      <button pButton type="button" severity="secondary" [label]="t('catalog.storefront.profile.ai.generate')" [disabled]="generating() || applyingPreview() || !storeId || !aiPrompt.trim()" (click)="generateDraft()"></button>
      @if (aiNotice()) { <p class="notice">{{ aiNotice() }}</p> }
    </section>
    }

    @if (aiPreview(); as preview) {
      <section class="ai-preview-panel">
        <ain-info-hint
          [titleKey]="'catalog.storefront.profile.ai.preview.title'"
          [descriptionKey]="'catalog.storefront.profile.ai.preview.description'"
          [bulletKeys]="[
            'catalog.storefront.profile.ai.preview.bulletReview',
            'catalog.storefront.profile.ai.preview.bulletEdit',
            'catalog.storefront.profile.ai.preview.bulletApply'
          ]" />

        @if (isStubProvider(preview)) {
          <div class="banner banner-warn">
            <strong>{{ t('catalog.storefront.profile.ai.stubNotice') }}</strong>
          </div>
        } @else if (preview.usedFallback) {
          <div class="banner banner-warn">
            <strong>{{ t('catalog.storefront.profile.ai.fallbackNotice') }}</strong>
          </div>
        } @else {
          <p class="notice">{{ t('catalog.storefront.profile.ai.generatedNotice') }}</p>
        }

        <p class="provider-hint">{{ t('catalog.storefront.profile.ai.preview.provider') }}: {{ preview.provider }}</p>

        @if (preview.guardrailViolations.length > 0) {
          <div class="guardrail-panel">
            <h4>{{ t('catalog.storefront.profile.guardrails.title') }}</h4>
            <ul>
              @for (item of preview.guardrailViolations; track item.code + item.message) {
                <li>{{ item.message }}</li>
              }
            </ul>
          </div>
        }

        <h4>{{ t('catalog.storefront.profile.section.identity') }}</h4>
        <label>{{ t('catalog.storefront.profile.brandName') }}<input pInputText [(ngModel)]="preview.profile.brandName" class="full" /></label>
        <label>{{ t('catalog.storefront.profile.tagline') }}<input pInputText [(ngModel)]="preview.profile.tagline" class="full" /></label>
        <label>{{ t('catalog.storefront.profile.shortDescription') }}<textarea pTextarea rows="3" [(ngModel)]="preview.profile.shortDescription" class="full"></textarea></label>

        <h4>{{ t('catalog.storefront.profile.section.contact') }}</h4>
        <label>{{ t('catalog.storefront.profile.phones') }}<textarea pTextarea rows="2" [ngModel]="preview.phonesText" (ngModelChange)="updatePreviewPhones($event)" class="full"></textarea></label>
        <label>{{ t('catalog.storefront.profile.emails') }}<textarea pTextarea rows="2" [ngModel]="preview.emailsText" (ngModelChange)="updatePreviewEmails($event)" class="full"></textarea></label>
        <label>{{ t('catalog.storefront.profile.address') }}<input pInputText [(ngModel)]="preview.profile.address" class="full" /></label>
        <label>{{ t('catalog.storefront.profile.hours') }}<input pInputText [(ngModel)]="preview.profile.hours" class="full" /></label>

        <h4>{{ t('catalog.storefront.profile.section.about') }}</h4>
        <label>{{ t('catalog.storefront.profile.aboutHeadline') }}<input pInputText [(ngModel)]="preview.profile.aboutHeadline" class="full" /></label>
        <label>{{ t('catalog.storefront.profile.aboutBody') }}<textarea pTextarea rows="4" [(ngModel)]="preview.profile.aboutBody" class="full"></textarea></label>

        <div class="list-head">
          <span>{{ t('catalog.storefront.profile.aboutHighlights') }}</span>
          <button pButton type="button" size="small" severity="secondary" [label]="t('common.add')" (click)="addPreviewHighlight()"></button>
        </div>
        @for (payload of preview.highlightPayloads; track $index) {
          <article class="list-card">
            <ain-structured-json-form [fields]="highlightFields" [payloadJson]="preview.highlightPayloads[$index]" (payloadJsonChange)="updatePreviewHighlight($index, $event)" />
            <button pButton type="button" size="small" severity="danger" [text]="true" [label]="t('common.remove')" (click)="removePreviewHighlight($index)"></button>
          </article>
        }

        <div class="list-head">
          <span>{{ t('catalog.storefront.profile.faqItems') }}</span>
          <button pButton type="button" size="small" severity="secondary" [label]="t('common.add')" (click)="addPreviewFaq()"></button>
        </div>
        @for (payload of preview.faqPayloads; track $index) {
          <article class="list-card">
            <ain-structured-json-form [fields]="faqFields" [payloadJson]="preview.faqPayloads[$index]" (payloadJsonChange)="updatePreviewFaq($index, $event)" />
            <button pButton type="button" size="small" severity="danger" [text]="true" [label]="t('common.remove')" (click)="removePreviewFaq($index)"></button>
          </article>
        }

        <div class="preview-actions">
          <button pButton type="button" severity="secondary" [label]="t('catalog.storefront.profile.ai.preview.cancel')" [disabled]="applyingPreview()" (click)="cancelAiPreview()"></button>
          <button pButton type="button" severity="success" icon="pi pi-check" [label]="t('catalog.storefront.profile.ai.preview.apply')" [disabled]="applyingPreview()" (click)="confirmApplyAiPreview()"></button>
        </div>
      </section>
    }

    <p-confirmDialog />

    <div class="form">
      <ain-info-hint
        [titleKey]="'catalog.storefront.profile.sectionHelp.identity.title'"
        [descriptionKey]="'catalog.storefront.profile.sectionHelp.identity.description'" />
      <h4>{{ t('catalog.storefront.profile.section.identity') }}</h4>
      <label>{{ t('catalog.storefront.profile.brandName') }}<input pInputText [(ngModel)]="profile.brandName" class="full" /></label>
      <label>{{ t('catalog.storefront.profile.tagline') }}<input pInputText [(ngModel)]="profile.tagline" class="full" /></label>
      <label>{{ t('catalog.storefront.profile.shortDescription') }}<textarea pTextarea rows="3" [(ngModel)]="profile.shortDescription" class="full"></textarea></label>

      <ain-info-hint
        [titleKey]="'catalog.storefront.profile.sectionHelp.contact.title'"
        [descriptionKey]="'catalog.storefront.profile.sectionHelp.contact.description'" />
      <h4>{{ t('catalog.storefront.profile.section.contact') }}</h4>
      <label>{{ t('catalog.storefront.profile.phones') }}<textarea pTextarea rows="2" [ngModel]="phonesText" (ngModelChange)="phonesText = $event; syncPhones()" class="full"></textarea></label>
      <label>{{ t('catalog.storefront.profile.emails') }}<textarea pTextarea rows="2" [ngModel]="emailsText" (ngModelChange)="emailsText = $event; syncEmails()" class="full"></textarea></label>
      <label>{{ t('catalog.storefront.profile.address') }}<input pInputText [(ngModel)]="profile.address" class="full" /></label>
      <label>{{ t('catalog.storefront.profile.hours') }}<input pInputText [(ngModel)]="profile.hours" class="full" /></label>

      <ain-info-hint
        [titleKey]="'catalog.storefront.profile.sectionHelp.social.title'"
        [descriptionKey]="'catalog.storefront.profile.sectionHelp.social.description'" />
      <h4>{{ t('catalog.storefront.profile.section.social') }}</h4>
      <ain-structured-json-form [fields]="socialFields" [(payloadJson)]="socialLinksJson" (payloadJsonChange)="syncSocialLinks()" />

      <ain-info-hint
        [titleKey]="'catalog.storefront.profile.sectionHelp.about.title'"
        [descriptionKey]="'catalog.storefront.profile.sectionHelp.about.description'" />
      <h4>{{ t('catalog.storefront.profile.section.about') }}</h4>
      <label>{{ t('catalog.storefront.profile.aboutHeadline') }}<input pInputText [(ngModel)]="profile.aboutHeadline" class="full" /></label>
      <label>{{ t('catalog.storefront.profile.aboutBody') }}<textarea pTextarea rows="4" [(ngModel)]="profile.aboutBody" class="full"></textarea></label>
      <div class="list-head">
        <span>{{ t('catalog.storefront.profile.aboutHighlights') }}</span>
        <button pButton type="button" size="small" severity="secondary" [label]="t('common.add')" (click)="addHighlight()"></button>
      </div>
      @for (payload of highlightPayloads; track $index) {
        <article class="list-card">
          <ain-structured-json-form [fields]="highlightFields" [(payloadJson)]="highlightPayloads[$index]" (payloadJsonChange)="syncHighlights()" />
          <button pButton type="button" size="small" severity="danger" [text]="true" [label]="t('common.remove')" (click)="removeHighlight($index)"></button>
        </article>
      }

      <ain-info-hint
        [titleKey]="'catalog.storefront.profile.sectionHelp.faq.title'"
        [descriptionKey]="'catalog.storefront.profile.sectionHelp.faq.description'" />
      <h4>{{ t('catalog.storefront.profile.section.faq') }}</h4>
      <div class="list-head">
        <span>{{ t('catalog.storefront.profile.faqItems') }}</span>
        <button pButton type="button" size="small" severity="secondary" [label]="t('common.add')" (click)="addFaq()"></button>
      </div>
      @for (payload of faqPayloads; track $index) {
        <article class="list-card">
          <ain-structured-json-form [fields]="faqFields" [(payloadJson)]="faqPayloads[$index]" (payloadJsonChange)="syncFaq()" />
          <button pButton type="button" size="small" severity="danger" [text]="true" [label]="t('common.remove')" (click)="removeFaq($index)"></button>
        </article>
      }

      <ain-info-hint
        [titleKey]="'catalog.storefront.profile.sectionHelp.support.title'"
        [descriptionKey]="'catalog.storefront.profile.sectionHelp.support.description'" />
      <h4>{{ t('catalog.storefront.profile.section.support') }}</h4>
      <label>{{ t('catalog.storefront.profile.contactSupportText') }}<textarea pTextarea rows="3" [(ngModel)]="profile.contactSupportText" class="full"></textarea></label>
      <div class="list-head">
        <span>{{ t('catalog.storefront.profile.contactMethods') }}</span>
        <button pButton type="button" size="small" severity="secondary" [label]="t('common.add')" (click)="addContactMethod()"></button>
      </div>
      @for (payload of contactPayloads; track $index) {
        <article class="list-card">
          <ain-structured-json-form [fields]="contactFields" [(payloadJson)]="contactPayloads[$index]" (payloadJsonChange)="syncContactMethods()" />
          <button pButton type="button" size="small" severity="danger" [text]="true" [label]="t('common.remove')" (click)="removeContactMethod($index)"></button>
        </article>
      }

      <ain-info-hint
        [titleKey]="'catalog.storefront.profile.sectionHelp.headerFooter.title'"
        [descriptionKey]="'catalog.storefront.profile.sectionHelp.headerFooter.description'" />
      <h4>{{ t('catalog.storefront.profile.section.headerFooter') }}</h4>
      <label>{{ t('catalog.storefront.profile.headerTopBarMessage') }}<input pInputText [(ngModel)]="profile.headerTopBarMessage" class="full" /></label>
      <div class="list-head">
        <span>{{ t('catalog.storefront.profile.headerQuickLinks') }}</span>
        <button pButton type="button" size="small" severity="secondary" [label]="t('common.add')" (click)="addHeaderLink()"></button>
      </div>
      @for (payload of headerLinkPayloads; track $index) {
        <article class="list-card">
          <ain-structured-json-form [fields]="linkFields" [(payloadJson)]="headerLinkPayloads[$index]" (payloadJsonChange)="syncHeaderLinks()" />
          <button pButton type="button" size="small" severity="danger" [text]="true" [label]="t('common.remove')" (click)="removeHeaderLink($index)"></button>
        </article>
      }
      <label>{{ t('catalog.storefront.profile.footerBrandText') }}<textarea pTextarea rows="2" [(ngModel)]="profile.footerBrandText" class="full"></textarea></label>
      <div class="list-head">
        <span>{{ t('catalog.storefront.profile.footerQuickLinks') }}</span>
        <button pButton type="button" size="small" severity="secondary" [label]="t('common.add')" (click)="addFooterLink()"></button>
      </div>
      @for (payload of footerLinkPayloads; track $index) {
        <article class="list-card">
          <ain-structured-json-form [fields]="linkFields" [(payloadJson)]="footerLinkPayloads[$index]" (payloadJsonChange)="syncFooterLinks()" />
          <button pButton type="button" size="small" severity="danger" [text]="true" [label]="t('common.remove')" (click)="removeFooterLink($index)"></button>
        </article>
      }
      <label>{{ t('catalog.storefront.profile.footerCopyright') }}<input pInputText [(ngModel)]="profile.footerCopyright" class="full" /></label>
      <p class="source-hint">{{ t(sourceLabelKey()) }}: {{ source }}</p>
    </div>
  `,
  styles: [`
    .form { display: grid; gap: 10px; }
    .form h4 { margin: 12px 0 0; font-size: 14px; }
    .full { width: 100%; }
    .ai-section { display: grid; gap: 8px; padding-bottom: 12px; border-bottom: 1px solid #e2e8f0; margin-bottom: 12px; }
    .ai-preview-panel { display: grid; gap: 10px; padding: 12px; border: 1px solid #bfdbfe; border-radius: 8px; background: #f8fafc; margin-bottom: 12px; }
    .ai-preview-panel h4 { margin: 8px 0 0; font-size: 14px; }
    .banner { border-radius: 10px; padding: 12px 14px; }
    .banner-warn { background: #fff7ed; border: 1px solid #fdba74; color: #9a3412; }
    .provider-hint { margin: 0; color: #64748b; font-size: 12px; }
    .preview-actions { display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap; margin-top: 8px; }
    .prompt-head { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .prompt-actions { display: flex; gap: 4px; flex-wrap: wrap; justify-content: flex-end; }
    .prompt-help { margin: 0; color: #64748b; font-size: 12px; }
    .growth-diagnostics { display: grid; gap: 6px; padding: 10px; border: 1px solid #e2e8f0; border-radius: 8px; background: #f8fafc; }
    .growth-diagnostics-head { display: flex; justify-content: space-between; gap: 8px; flex-wrap: wrap; font-size: 12px; color: #334155; }
    .growth-diagnostics ul { margin: 0; padding: 0; list-style: none; display: grid; gap: 4px; }
    .growth-diagnostics li { display: flex; justify-content: space-between; gap: 8px; font-size: 12px; padding: 4px 0; border-bottom: 1px dashed #e2e8f0; }
    .growth-diagnostics li:last-child { border-bottom: 0; }
    .growth-diagnostic-status { color: #64748b; white-space: nowrap; }
    .growth-diagnostic-available .growth-diagnostic-status { color: #15803d; }
    .growth-diagnostic-partial .growth-diagnostic-status { color: #b45309; }
    .growth-diagnostic-missing .growth-diagnostic-status { color: #b91c1c; }
    .ai-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
    .list-head { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .list-card { display: grid; gap: 8px; padding: 10px; border: 1px solid #e2e8f0; border-radius: 8px; }
    .source-hint { margin-top: 8px; color: #64748b; font-size: 0.875rem; }
    .notice { margin: 0; color: #475569; font-size: 13px; }
    .lifecycle-toolbar { display: grid; gap: 8px; margin-bottom: 12px; padding-bottom: 12px; border-bottom: 1px solid #e2e8f0; }
    .lifecycle-actions { display: flex; flex-wrap: wrap; gap: 6px; }
    .guardrail-panel { padding: 10px; border: 1px solid #fecaca; border-radius: 8px; background: #fff1f2; margin-bottom: 12px; }
    .guardrail-panel--warn { border-color: #fde68a; background: #fffbeb; }
    .meta-section { display: grid; gap: 8px; margin-bottom: 12px; padding-bottom: 12px; border-bottom: 1px solid #e2e8f0; }
    .version-history ul { margin: 0; padding-left: 18px; }
    label { display: grid; gap: 4px; color: #475569; font-size: 12px; font-weight: 600; }
  `]
})
export class StorefrontProfileEditorComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly confirmation = inject(ConfirmationService);

  @Input() storeId?: string | null;
  @Input() editorMode: 'store' | 'tenant-default' = 'store';
  @Input() source = 'system';
  @Output() profileChange = new EventEmitter<StorefrontContentProfile>();
  @Output() lifecycleChanged = new EventEmitter<void>();

  readonly generating = signal(false);
  readonly composingGrowthPrompt = signal(false);
  readonly applyingPreview = signal(false);
  readonly acting = signal(false);
  readonly aiNotice = signal('');
  readonly growthDiagnostics = signal<ComposeStorefrontGrowthPromptDiagnostic[]>([]);
  readonly growthCompleteness = signal(0);
  readonly aiPreview = signal<StorefrontAiPreviewState | null>(null);
  readonly statusNotice = signal('');
  readonly guardrailViolations = signal<{ code: string; message: string }[]>([]);
  readonly versions = signal<StorefrontProfileVersionSummary[]>([]);
  readonly draftState = signal<{ isAiGenerated: boolean; approvalStatus: string; approvalNotes?: string | null } | null>(null);

  localeOptions = LOCALE_OPTIONS;
  selectedLocale = 'fa-IR';
  viewMode: 'draft' | 'published' = 'draft';
  metadata: StorefrontProfileMetadata = normalizeStorefrontProfileMetadata();
  private isAiDraft = false;

  profile: StorefrontContentProfile = EMPTY_PROFILE();
  phonesText = '';
  emailsText = '';
  socialLinksJson = '{}';
  highlightPayloads: string[] = [];
  faqPayloads: string[] = [];
  contactPayloads: string[] = [];
  headerLinkPayloads: string[] = [];
  footerLinkPayloads: string[] = [];

  aiPrompt = '';
  aiIndustry = '';
  aiAudience = '';
  aiTone = '';
  aiLanguage = '';

  readonly socialFields: StructuredJsonFieldDef[] = [
    { key: 'instagram', type: 'string', labelKey: 'catalog.storefront.profile.social.instagram' },
    { key: 'youTube', type: 'string', labelKey: 'catalog.storefront.profile.social.youtube' },
    { key: 'telegram', type: 'string', labelKey: 'catalog.storefront.profile.social.telegram' },
    { key: 'linkedIn', type: 'string', labelKey: 'catalog.storefront.profile.social.linkedin' },
    { key: 'twitter', type: 'string', labelKey: 'catalog.storefront.profile.social.twitter' },
    { key: 'website', type: 'string', labelKey: 'catalog.storefront.profile.social.website' }
  ];
  readonly highlightFields: StructuredJsonFieldDef[] = [
    { key: 'title', type: 'string', labelKey: 'catalog.storefront.profile.highlight.title' },
    { key: 'body', type: 'string', labelKey: 'catalog.storefront.profile.highlight.body' }
  ];
  readonly faqFields: StructuredJsonFieldDef[] = [
    { key: 'question', type: 'string', labelKey: 'catalog.storefront.profile.faq.question' },
    { key: 'answer', type: 'string', labelKey: 'catalog.storefront.profile.faq.answer' },
    { key: 'category', type: 'string', labelKey: 'catalog.storefront.profile.faq.category' }
  ];
  readonly contactFields: StructuredJsonFieldDef[] = [
    { key: 'title', type: 'string', labelKey: 'catalog.storefront.profile.contactMethod.title' },
    { key: 'body', type: 'string', labelKey: 'catalog.storefront.profile.contactMethod.body' },
    { key: 'actionLabel', type: 'string', labelKey: 'catalog.storefront.profile.contactMethod.actionLabel' },
    { key: 'actionUrl', type: 'string', labelKey: 'catalog.storefront.profile.contactMethod.actionUrl' }
  ];
  readonly linkFields: StructuredJsonFieldDef[] = [
    { key: 'label', type: 'string', labelKey: 'catalog.storefront.profile.link.label' },
    { key: 'url', type: 'string', labelKey: 'catalog.storefront.profile.link.url' }
  ];

  helpTitleKey(): string {
    return this.editorMode === 'tenant-default'
      ? 'catalog.storefront.profile.tenantDefault.help.title'
      : 'catalog.storefront.profile.help.title';
  }

  helpDescriptionKey(): string {
    return this.editorMode === 'tenant-default'
      ? 'catalog.storefront.profile.tenantDefault.help.description'
      : 'catalog.storefront.profile.help.description';
  }

  helpBulletKeys(): string[] {
    return this.editorMode === 'tenant-default'
      ? [
        'catalog.storefront.profile.tenantDefault.help.bullets.fallback',
        'catalog.storefront.profile.tenantDefault.help.bullets.stores',
        'catalog.storefront.profile.tenantDefault.help.bullets.review'
      ]
      : [
        'catalog.storefront.profile.help.bullets.unified',
        'catalog.storefront.profile.help.bullets.fallback',
        'catalog.storefront.profile.help.bullets.review'
      ];
  }

  sourceLabelKey(): string {
    return this.editorMode === 'tenant-default'
      ? 'catalog.storefront.profile.tenantDefault.source'
      : 'catalog.storefront.profile.source';
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  ngOnInit(): void {
    if (this.editorMode === 'store' && !this.aiPrompt.trim()) {
      this.applyPromptTemplate();
    }
    this.loadLifecycleState();
  }

  setViewMode(mode: 'draft' | 'published'): void {
    this.viewMode = mode;
    this.loadLifecycleState();
  }

  loadLifecycleState(): void {
    const locale = this.selectedLocale;
    const versions$ = this.editorMode === 'store' && this.storeId
      ? this.service.listStoreProfileVersions(this.storeId, locale)
      : this.service.listTenantProfileVersions(locale);

    versions$.subscribe({
      next: items => this.versions.set(items),
      error: () => this.versions.set([])
    });

    if (this.viewMode === 'published') {
      const published$ = this.editorMode === 'store' && this.storeId
        ? this.service.getStoreProfilePublished(this.storeId, locale)
        : this.service.getTenantProfilePublished(locale);
      published$.subscribe({
        next: response => {
          this.metadata = normalizeStorefrontProfileMetadata(response.metadata);
          this.applyProfile(response.profile, response.source);
          this.guardrailViolations.set([]);
          this.draftState.set(null);
        }
      });
      return;
    }

    const draft$ = this.editorMode === 'store' && this.storeId
      ? this.service.getStoreProfileDraft(this.storeId, locale)
      : this.service.getTenantProfileDraft(locale);

    draft$.subscribe({
      next: draft => {
        this.metadata = normalizeStorefrontProfileMetadata(draft.metadata);
        this.isAiDraft = draft.isAiGenerated;
        this.draftState.set({
          isAiGenerated: draft.isAiGenerated,
          approvalStatus: draft.approvalStatus,
          approvalNotes: draft.approvalNotes
        });
        this.guardrailViolations.set(draft.guardrailViolations ?? []);
        this.applyProfile(draft.profile, 'draft');
      },
      error: (error: unknown) => {
        if (error instanceof HttpErrorResponse && error.status === 404) {
          this.applyEmptyDraftState();
          return;
        }

        this.draftState.set(null);
        this.guardrailViolations.set([]);
        this.statusNotice.set(this.t('catalog.storefront.profile.error'));
      }
    });
  }

  saveDraft(): void {
    this.acting.set(true);
    const payload = {
      locale: this.selectedLocale,
      profile: this.buildProfile(),
      metadata: this.metadata,
      isAiGenerated: this.isAiDraft
    };
    const request$ = this.editorMode === 'store' && this.storeId
      ? this.service.saveStoreProfileDraft(this.storeId, payload)
      : this.service.saveTenantProfileDraft(payload);

    request$.subscribe({
      next: draft => {
        this.metadata = normalizeStorefrontProfileMetadata(draft.metadata ?? this.metadata);
        this.isAiDraft = draft.isAiGenerated;
        this.draftState.set({
          isAiGenerated: draft.isAiGenerated,
          approvalStatus: draft.approvalStatus,
          approvalNotes: draft.approvalNotes
        });
        this.guardrailViolations.set(draft.guardrailViolations ?? []);
        this.statusNotice.set(this.t('catalog.storefront.profile.savedDraft'));
        this.acting.set(false);
        this.lifecycleChanged.emit();
        this.loadLifecycleState();
      },
      error: () => {
        this.statusNotice.set(this.t('catalog.storefront.profile.error'));
        this.acting.set(false);
      }
    });
  }

  publishDraft(): void {
    this.acting.set(true);
    const request$ = this.editorMode === 'store' && this.storeId
      ? this.service.publishStoreProfileDraft(this.storeId, this.selectedLocale)
      : this.service.publishTenantProfileDraft(this.selectedLocale);
    request$.subscribe({
      next: () => {
        this.statusNotice.set(this.t('catalog.storefront.profile.published'));
        this.isAiDraft = false;
        this.acting.set(false);
        this.lifecycleChanged.emit();
        this.loadLifecycleState();
      },
      error: () => {
        this.statusNotice.set(this.t('catalog.storefront.profile.publishBlocked'));
        this.acting.set(false);
      }
    });
  }

  submitApproval(): void {
    if (!this.storeId || this.editorMode !== 'store') return;
    this.acting.set(true);
    this.service.submitStoreProfileApproval(this.storeId, this.selectedLocale).subscribe({
      next: () => {
        this.statusNotice.set(this.t('catalog.storefront.profile.submittedApproval'));
        this.acting.set(false);
        this.loadLifecycleState();
      },
      error: () => this.acting.set(false)
    });
  }

  approveDraft(): void {
    if (!this.storeId || this.editorMode !== 'store') return;
    this.acting.set(true);
    this.service.approveStoreProfileDraft(this.storeId, { notes: null }, this.selectedLocale).subscribe({
      next: () => {
        this.statusNotice.set(this.t('catalog.storefront.profile.approved'));
        this.acting.set(false);
        this.loadLifecycleState();
      },
      error: () => this.acting.set(false)
    });
  }

  rejectDraft(): void {
    if (!this.storeId || this.editorMode !== 'store') return;
    this.acting.set(true);
    this.service.rejectStoreProfileDraft(this.storeId, { notes: null }, this.selectedLocale).subscribe({
      next: () => {
        this.statusNotice.set(this.t('catalog.storefront.profile.rejected'));
        this.acting.set(false);
        this.loadLifecycleState();
      },
      error: () => this.acting.set(false)
    });
  }

  rollback(version: StorefrontProfileVersionSummary): void {
    this.acting.set(true);
    const payload = { versionId: version.id, locale: this.selectedLocale };
    const request$ = this.editorMode === 'store' && this.storeId
      ? this.service.rollbackStoreProfile(this.storeId, payload)
      : this.service.rollbackTenantProfile(payload);
    request$.subscribe({
      next: () => {
        this.statusNotice.set(this.t('catalog.storefront.profile.rolledBack'));
        this.acting.set(false);
        this.lifecycleChanged.emit();
        this.loadLifecycleState();
      },
      error: () => this.acting.set(false)
    });
  }

  canPublish(): boolean {
    const draft = this.draftState();
    if (!draft?.isAiGenerated) return true;
    return draft.approvalStatus === 'Approved';
  }

  applyPromptTemplate(): void {
    this.aiPrompt = this.t('catalog.storefront.profile.ai.promptTemplate');
  }

  buildFromGrowthData(): void {
    if (!this.storeId) return;
    this.composingGrowthPrompt.set(true);
    this.aiNotice.set('');
    this.service.composeStoreGrowthPrompt(this.storeId, this.selectedLocale, this.buildProfile()).subscribe({
      next: result => {
        this.growthDiagnostics.set(result.diagnostics ?? []);
        this.growthCompleteness.set(result.completenessPercent ?? 0);
        if (!result.hasSourceData || !result.prompt?.trim()) {
          this.aiNotice.set(this.t('catalog.storefront.profile.ai.growthNoData'));
          this.composingGrowthPrompt.set(false);
          return;
        }

        this.aiPrompt = result.prompt.trim();
        if (result.suggestedIndustry?.trim()) this.aiIndustry = result.suggestedIndustry.trim();
        if (result.suggestedAudience?.trim()) this.aiAudience = result.suggestedAudience.trim();
        if (result.suggestedTone?.trim()) this.aiTone = result.suggestedTone.trim();
        if (result.suggestedLanguage?.trim()) this.aiLanguage = result.suggestedLanguage.trim();
        this.aiNotice.set(this.t('catalog.storefront.profile.ai.growthBuiltNotice'));
        this.composingGrowthPrompt.set(false);
      },
      error: () => {
        this.aiNotice.set(this.t('catalog.storefront.profile.ai.growthError'));
        this.composingGrowthPrompt.set(false);
      }
    });
  }

  growthDiagnosticLabel(item: ComposeStorefrontGrowthPromptDiagnostic): string {
    return this.t(`catalog.storefront.profile.ai.growthDiagnostics.section.${item.sectionKey}`);
  }

  growthDiagnosticStatus(item: ComposeStorefrontGrowthPromptDiagnostic): string {
    return this.t(`catalog.storefront.profile.ai.growthDiagnostics.status.${item.status}`);
  }

  growthDiagnosticClass(item: ComposeStorefrontGrowthPromptDiagnostic): string {
    const status = item.status?.toLowerCase() ?? 'missing';
    if (status === 'available') return 'growth-diagnostic-available';
    if (status === 'partial') return 'growth-diagnostic-partial';
    return 'growth-diagnostic-missing';
  }

  private applyEmptyDraftState(): void {
    this.metadata = normalizeStorefrontProfileMetadata();
    this.isAiDraft = false;
    this.draftState.set(null);
    this.guardrailViolations.set([]);
    this.applyProfile(EMPTY_PROFILE(), 'draft');
  }

  applyProfile(value: StorefrontContentProfile, source: string): void {
    this.source = source;
    this.profile = {
      ...EMPTY_PROFILE(),
      ...value,
      phones: value.phones ?? [],
      emails: value.emails ?? [],
      aboutHighlights: value.aboutHighlights ?? [],
      faqItems: value.faqItems ?? [],
      contactMethods: value.contactMethods ?? [],
      headerQuickLinks: value.headerQuickLinks ?? [],
      footerQuickLinks: value.footerQuickLinks ?? []
    };
    this.phonesText = this.profile.phones.join('\n');
    this.emailsText = this.profile.emails.join('\n');
    this.socialLinksJson = JSON.stringify(value.socialLinks ?? {});
    this.highlightPayloads = this.profile.aboutHighlights.map(item => JSON.stringify(item));
    this.faqPayloads = this.profile.faqItems.map(item => JSON.stringify(item));
    this.contactPayloads = this.profile.contactMethods.map(item => JSON.stringify(item));
    this.headerLinkPayloads = this.profile.headerQuickLinks.map(item => JSON.stringify(item));
    this.footerLinkPayloads = this.profile.footerQuickLinks.map(item => JSON.stringify(item));
    this.emitProfile();
  }

  buildProfile(): StorefrontContentProfile {
    this.syncPhones(false);
    this.syncEmails(false);
    this.syncSocialLinks(false);
    this.syncHighlights(false);
    this.syncFaq(false);
    this.syncContactMethods(false);
    this.syncHeaderLinks(false);
    this.syncFooterLinks(false);
    return this.profile;
  }

  generateDraft(): void {
    if (!this.storeId || !this.aiPrompt.trim()) return;
    this.generating.set(true);
    this.aiNotice.set('');
    this.service.generateStoreProfileDraft(this.storeId, {
      brandDescription: this.aiPrompt.trim(),
      industry: this.aiIndustry.trim() || null,
      audience: this.aiAudience.trim() || null,
      tone: this.aiTone.trim() || null,
      language: this.aiLanguage.trim() || null
    }).subscribe({
      next: result => {
        this.aiPreview.set(this.buildPreviewState(result));
        this.aiNotice.set(this.t('catalog.storefront.profile.ai.preview.readyNotice'));
        this.generating.set(false);
      },
      error: () => {
        this.aiNotice.set(this.t('catalog.storefront.profile.ai.error'));
        this.generating.set(false);
      }
    });
  }

  cancelAiPreview(): void {
    this.aiPreview.set(null);
    this.aiNotice.set('');
  }

  confirmApplyAiPreview(): void {
    const preview = this.aiPreview();
    if (!preview) return;

    this.confirmation.confirm({
      header: this.t('catalog.storefront.profile.ai.applyConfirm.title'),
      message: this.t('catalog.storefront.profile.ai.applyConfirm.message'),
      accept: () => this.applyAiPreview()
    });
  }

  isStubProvider(preview: StorefrontAiPreviewState): boolean {
    const provider = preview.provider?.trim().toLowerCase() ?? '';
    return provider === 'stub' || provider === 'none';
  }

  updatePreviewPhones(value: string): void {
    const preview = this.aiPreview();
    if (!preview) return;
    this.aiPreview.set({
      ...preview,
      phonesText: value,
      profile: {
        ...preview.profile,
        phones: value.split('\n').map(item => item.trim()).filter(Boolean)
      }
    });
  }

  updatePreviewEmails(value: string): void {
    const preview = this.aiPreview();
    if (!preview) return;
    this.aiPreview.set({
      ...preview,
      emailsText: value,
      profile: {
        ...preview.profile,
        emails: value.split('\n').map(item => item.trim()).filter(Boolean)
      }
    });
  }

  addPreviewHighlight(): void {
    const preview = this.aiPreview();
    if (!preview) return;
    this.aiPreview.set({ ...preview, highlightPayloads: [...preview.highlightPayloads, '{}'] });
  }

  removePreviewHighlight(index: number): void {
    const preview = this.aiPreview();
    if (!preview) return;
    const payloads = [...preview.highlightPayloads];
    payloads.splice(index, 1);
    this.aiPreview.set({ ...preview, highlightPayloads: payloads, profile: this.buildPreviewProfile({ ...preview, highlightPayloads: payloads }) });
  }

  updatePreviewHighlight(index: number, value: string): void {
    const preview = this.aiPreview();
    if (!preview) return;
    const payloads = [...preview.highlightPayloads];
    payloads[index] = value;
    this.aiPreview.set({ ...preview, highlightPayloads: payloads, profile: this.buildPreviewProfile({ ...preview, highlightPayloads: payloads }) });
  }

  addPreviewFaq(): void {
    const preview = this.aiPreview();
    if (!preview) return;
    this.aiPreview.set({ ...preview, faqPayloads: [...preview.faqPayloads, '{}'] });
  }

  removePreviewFaq(index: number): void {
    const preview = this.aiPreview();
    if (!preview) return;
    const payloads = [...preview.faqPayloads];
    payloads.splice(index, 1);
    this.aiPreview.set({ ...preview, faqPayloads: payloads, profile: this.buildPreviewProfile({ ...preview, faqPayloads: payloads }) });
  }

  updatePreviewFaq(index: number, value: string): void {
    const preview = this.aiPreview();
    if (!preview) return;
    const payloads = [...preview.faqPayloads];
    payloads[index] = value;
    this.aiPreview.set({ ...preview, faqPayloads: payloads, profile: this.buildPreviewProfile({ ...preview, faqPayloads: payloads }) });
  }

  private applyAiPreview(): void {
    const preview = this.aiPreview();
    if (!this.storeId || !preview) return;

    this.applyingPreview.set(true);
    const profile = this.buildPreviewProfile(preview);
    this.service.saveStoreProfileDraft(this.storeId, {
      locale: this.selectedLocale,
      profile,
      metadata: preview.metadata,
      isAiGenerated: preview.requiresApproval
    }).subscribe({
      next: draft => {
        this.aiPreview.set(null);
        this.aiNotice.set('');
        this.metadata = normalizeStorefrontProfileMetadata(draft.metadata ?? preview.metadata);
        this.isAiDraft = draft.isAiGenerated;
        this.draftState.set({
          isAiGenerated: draft.isAiGenerated,
          approvalStatus: draft.approvalStatus,
          approvalNotes: draft.approvalNotes
        });
        this.guardrailViolations.set(draft.guardrailViolations ?? preview.guardrailViolations);
        this.statusNotice.set(this.t('catalog.storefront.profile.ai.appliedNotice'));
        this.applyingPreview.set(false);
        this.lifecycleChanged.emit();
        this.loadLifecycleState();
      },
      error: () => {
        this.statusNotice.set(this.t('catalog.storefront.profile.error'));
        this.applyingPreview.set(false);
      }
    });
  }

  private buildPreviewState(result: {
    profile: StorefrontContentProfile;
    metadata: StorefrontProfileMetadata;
    guardrailViolations: { code: string; message: string }[];
    provider: string;
    usedFallback: boolean;
    requiresApproval: boolean;
  }): StorefrontAiPreviewState {
    const profile = {
      ...EMPTY_PROFILE(),
      ...result.profile,
      phones: result.profile.phones ?? [],
      emails: result.profile.emails ?? [],
      aboutHighlights: result.profile.aboutHighlights ?? [],
      faqItems: result.profile.faqItems ?? [],
      contactMethods: result.profile.contactMethods ?? [],
      headerQuickLinks: result.profile.headerQuickLinks ?? [],
      footerQuickLinks: result.profile.footerQuickLinks ?? []
    };

    return {
      profile,
      metadata: normalizeStorefrontProfileMetadata(result.metadata),
      guardrailViolations: result.guardrailViolations ?? [],
      provider: result.provider,
      usedFallback: result.usedFallback,
      requiresApproval: result.requiresApproval,
      phonesText: profile.phones.join('\n'),
      emailsText: profile.emails.join('\n'),
      socialLinksJson: JSON.stringify(result.profile.socialLinks ?? {}),
      highlightPayloads: profile.aboutHighlights.map(item => JSON.stringify(item)),
      faqPayloads: profile.faqItems.map(item => JSON.stringify(item)),
      contactPayloads: profile.contactMethods.map(item => JSON.stringify(item)),
      headerLinkPayloads: profile.headerQuickLinks.map(item => JSON.stringify(item)),
      footerLinkPayloads: profile.footerQuickLinks.map(item => JSON.stringify(item))
    };
  }

  private buildPreviewProfile(preview: StorefrontAiPreviewState): StorefrontContentProfile {
    const highlights = preview.highlightPayloads
      .map(item => this.parseJson<StorefrontProfileHighlight>(item))
      .filter((item): item is StorefrontProfileHighlight => !!item?.title);
    const faqItems = preview.faqPayloads
      .map(item => this.parseJson<StorefrontProfileFaqItem>(item))
      .filter((item): item is StorefrontProfileFaqItem => !!item?.question);
    const contactMethods = preview.contactPayloads
      .map(item => this.parseJson<StorefrontProfileContactMethod>(item))
      .filter((item): item is StorefrontProfileContactMethod => !!item?.title);
    const headerQuickLinks = preview.headerLinkPayloads
      .map(item => this.parseJson<StorefrontProfileLink>(item))
      .filter((item): item is StorefrontProfileLink => !!item?.label && !!item?.url);
    const footerQuickLinks = preview.footerLinkPayloads
      .map(item => this.parseJson<StorefrontProfileLink>(item))
      .filter((item): item is StorefrontProfileLink => !!item?.label && !!item?.url);

    let socialLinks = preview.profile.socialLinks ?? {};
    try {
      socialLinks = JSON.parse(preview.socialLinksJson || '{}');
    } catch {
      socialLinks = {};
    }

    return {
      ...preview.profile,
      phones: preview.phonesText.split('\n').map(item => item.trim()).filter(Boolean),
      emails: preview.emailsText.split('\n').map(item => item.trim()).filter(Boolean),
      socialLinks,
      aboutHighlights: highlights,
      faqItems,
      contactMethods,
      headerQuickLinks,
      footerQuickLinks
    };
  }

  syncPhones(emit = true): void {
    this.profile.phones = this.phonesText.split('\n').map(item => item.trim()).filter(Boolean);
    if (emit) this.emitProfile();
  }

  syncEmails(emit = true): void {
    this.profile.emails = this.emailsText.split('\n').map(item => item.trim()).filter(Boolean);
    if (emit) this.emitProfile();
  }

  syncSocialLinks(emit = true): void {
    try {
      this.profile.socialLinks = JSON.parse(this.socialLinksJson || '{}');
    } catch {
      this.profile.socialLinks = {};
    }
    if (emit) this.emitProfile();
  }

  syncHighlights(emit = true): void {
    this.profile.aboutHighlights = this.highlightPayloads
      .map(item => this.parseJson<StorefrontProfileHighlight>(item))
      .filter((item): item is StorefrontProfileHighlight => !!item?.title);
    if (emit) this.emitProfile();
  }

  syncFaq(emit = true): void {
    this.profile.faqItems = this.faqPayloads
      .map(item => this.parseJson<StorefrontProfileFaqItem>(item))
      .filter((item): item is StorefrontProfileFaqItem => !!item?.question);
    if (emit) this.emitProfile();
  }

  syncContactMethods(emit = true): void {
    this.profile.contactMethods = this.contactPayloads
      .map(item => this.parseJson<StorefrontProfileContactMethod>(item))
      .filter((item): item is StorefrontProfileContactMethod => !!item?.title);
    if (emit) this.emitProfile();
  }

  syncHeaderLinks(emit = true): void {
    this.profile.headerQuickLinks = this.headerLinkPayloads
      .map(item => this.parseJson<StorefrontProfileLink>(item))
      .filter((item): item is StorefrontProfileLink => !!item?.label && !!item?.url);
    if (emit) this.emitProfile();
  }

  syncFooterLinks(emit = true): void {
    this.profile.footerQuickLinks = this.footerLinkPayloads
      .map(item => this.parseJson<StorefrontProfileLink>(item))
      .filter((item): item is StorefrontProfileLink => !!item?.label && !!item?.url);
    if (emit) this.emitProfile();
  }

  addHighlight(): void { this.highlightPayloads = [...this.highlightPayloads, '{}']; }
  removeHighlight(index: number): void { this.highlightPayloads.splice(index, 1); this.syncHighlights(); }
  addFaq(): void { this.faqPayloads = [...this.faqPayloads, '{}']; }
  removeFaq(index: number): void { this.faqPayloads.splice(index, 1); this.syncFaq(); }
  addContactMethod(): void { this.contactPayloads = [...this.contactPayloads, '{}']; }
  removeContactMethod(index: number): void { this.contactPayloads.splice(index, 1); this.syncContactMethods(); }
  addHeaderLink(): void { this.headerLinkPayloads = [...this.headerLinkPayloads, '{}']; }
  removeHeaderLink(index: number): void { this.headerLinkPayloads.splice(index, 1); this.syncHeaderLinks(); }
  addFooterLink(): void { this.footerLinkPayloads = [...this.footerLinkPayloads, '{}']; }
  removeFooterLink(index: number): void { this.footerLinkPayloads.splice(index, 1); this.syncFooterLinks(); }

  private emitProfile(): void {
    this.profileChange.emit(this.buildProfile());
  }

  private parseJson<T>(value: string): T | null {
    try {
      return JSON.parse(value || '{}') as T;
    } catch {
      return null;
    }
  }
}
